/* eslint-disable camelcase */

// node modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { stringify } from 'qs';
import { Query } from '@apollo/client/react/components';

// app modules: components
import Advert from 'components/Advert';
import ContentOptions from 'components/ContentOptions';
import AddToCollectionButton from 'components/AddToCollectionButton';
import { SearchInPerson } from 'components/SearchIn';
import DetailedCollectionItem from 'components/SearchResultsItem/Collection/DetailedCollectionItem';
import AuthorBooks from 'components/AuthorBooks';
import AuthorFavourites from 'components/AuthorFavourites';
import AuthorFollowLinks from 'components/AuthorFollowLinks';
import BlogPosts from 'components/BlogPosts';
import FavouriteButton from 'components/FavouriteButton';
import HeadContent from 'components/HeadContent';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Image from 'components/Image';
import PulseLoader from 'react-spinners/PulseLoader';
import RelatedPeople from 'components/RelatedPeople';
import ShowMore from 'components/ShowMore';
import TopRecipes from 'components/TopRecipes';

// app modules: props
import AuthorBooksProps from 'components/AuthorBooks/AuthorBooksProps';

// app modules: services
import { sendEvent } from 'services/analytics';
import assets from 'services/assets';
import history from 'services/history';
import route from 'services/route';

// app modules: constants
import { CONTENT_TYPES, COLOURS } from 'constants/index';

// app modules: queries
import COLLECTIONS_QUERY from './PersonCollectionsGraphQL';

// assets
import CssPerson from './Person.scss';

class Person extends Component {
  static propTypes = {
    baseUrl: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
    displayType: PropTypes.string.isRequired,
    forceSsr: PropTypes.bool,
    person: PropTypes.shape({
      id: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      title: PropTypes.string,
      biography: PropTypes.string,
      avatar_name: PropTypes.string,
      website: PropTypes.string,
      books: AuthorBooksProps.books,
      appearsIn: AuthorBooksProps.books,
      is_author: PropTypes.bool,
      facebook: PropTypes.string,
      twitter: PropTypes.string,
      instagram: PropTypes.string,
      spotify: PropTypes.string,
      app_user_id: PropTypes.number,
      licensed_book_count: PropTypes.number,
      book_count: PropTypes.number,
      appears_in_count: PropTypes.number,
      recommendations: PropTypes.arrayOf(
        PropTypes.shape({
          comment: PropTypes.string,
          ranking: PropTypes.number,
          book: PropTypes.shape({
            isbn: PropTypes.string,
            title_short: PropTypes.string,
            image_name: PropTypes.string,
            signature_: PropTypes.string,
            indexable: PropTypes.bool,
            licensed: PropTypes.bool,
            authors: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string,
                first_name: PropTypes.string,
                last_name: PropTypes.string,
              }),
            ),
          }),
        }),
      ),
    }),
    debug: PropTypes.bool,
  };

  static defaultProps = {
    person: {
      first_name: 'Missing',
      last_name: 'Missing',
      books: [],
      recommendations: [],
    },
    debug: false,
    forceSsr: false,
  };

  constructor(props, context) {
    super(props, context);

    this.bound = {
      searchPersonRecipes: this.searchPersonRecipes.bind(this),
    };

    this.state = {
      shouldShowSideAdvert: false,
    };
    this.advertRef = React.createRef();
    if (typeof window !== 'undefined' && window.IntersectionObserver) {
      this.advertObserver = new IntersectionObserver(this.onAdvertMove);
    }
  }

  componentDidMount() {
    const { id } = this.props.person;

    sendEvent({
      event: 'Content.Person.View',
      id,
    });
    if (this.advertRef.current) {
      this.advertObserver.observe(this.advertRef.current);
    }
  }

  componentDidUpdate(prevProps) {
    const { id } = this.props.person;

    if (id !== prevProps.person.id) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ shouldShowSideAdvert: false });
    }
    // Keep an eye on the position of the sidebar advert slot
    this.advertObserver.disconnect();
    if (this.advertRef.current) {
      this.advertObserver.observe(this.advertRef.current);
    }
  }

  componentWillUnmount() {
    if (this.advertObserver) {
      this.advertObserver.disconnect();
    }
  }

  onAdvertMove = () => {
    // Only display the side advert if its more than 500px from the top.
    // This will depend on how many author-books load (async) above it so we will use
    // IntersectionObserver to only display the advert if it passes the y=500 threshold.
    const { shouldShowSideAdvert } = this.state;
    if (
      !shouldShowSideAdvert &&
      this.advertRef.current?.getBoundingClientRect().top > 600 &&
      document.body.clientHeight > 2000
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ shouldShowSideAdvert: true });
      this.advertObserver.disconnect();
    }
  };

  getAllRecipesLink = () => {
    const { first_name, last_name } = this.props.person;
    const personName = `${first_name} ${last_name}`;
    const queryParams = stringify(
      { q: 'recipes', 'authors[0]': personName, sort: 'popularity' },
      {
        encodeValuesOnly: true,
      },
    );
    return `/search?${queryParams}`;
  };

  searchPersonRecipes(event) {
    if (event) {
      event.preventDefault();
    }
    history.push(this.getAllRecipesLink(), { referrer: 'Author', section: 'Search' });
  }

  renderSearch() {
    const { first_name, last_name, is_author } = this.props.person;
    const personName = `${first_name} ${last_name}`;

    if (is_author) {
      return <SearchInPerson name={personName} className="margin-bottom-40" />;
    }

    return null;
  }

  renderBiography() {
    const { biography } = this.props.person;
    const bioLength = biography.length;

    if (biography) {
      return (
        <ShowMore className="margin-bottom-40" childrenLength={bioLength}>
          <div className="person__biography">{biography}</div>
        </ShowMore>
      );
    }

    return null;
  }

  renderAuthorFavourites() {
    const { t } = this.props;
    const { first_name, recommendations } = this.props.person;

    if (recommendations.length > 0) {
      return (
        <section className="margin-bottom-40">
          <Heading withDash level={2} className="margin-bottom-20">
            {first_name}&#39;s {t('favorite')} cookbooks
          </Heading>
          <AuthorFavourites recommendations={recommendations} />
        </section>
      );
    }

    return null;
  }

  renderCollectionsQueryResult = ({ loading, error, data }) => {
    const { first_name, id } = this.props.person;
    if (loading) {
      return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <PulseLoader loading color={COLOURS.STABLE} sizeUnit="px" size={10} />
        </div>
      );
    }
    if (error) {
      console.error(error.message);
      return null;
    }
    if (data.Collections && data.Collections.length) {
      return (
        <section className="margin-bottom-40">
          <header className="margin-bottom-20">
            <Heading withDash level={2}>
              {first_name}&#39;s collections
            </Heading>
          </header>

          <div className="person__collections">
            {data.Collections.map(collection => (
              <DetailedCollectionItem
                key={`person-${id}-collection-${collection.id}`}
                item={{
                  ...collection,
                  title: collection.name,
                  description: collection.plain_description,
                  item_count: collection.items_count.total,
                  image: collection.custom_cover_image
                    ? { src: collection.custom_cover_image }
                    : collection.cover_image,
                }}
              />
            ))}
          </div>
        </section>
      );
    }
    return null;
  };

  renderAuthorCollections() {
    const { app_user_id } = this.props.person;

    if (app_user_id) {
      return (
        <Query query={COLLECTIONS_QUERY} variables={{ userId: app_user_id, limit: 10, offset: 0 }}>
          {this.renderCollectionsQueryResult}
        </Query>
      );
    }
    return null;
  }

  renderAppearsIn() {
    const { id, licensed_book_count, appears_in_count } = this.props.person;
    if (appears_in_count > 0) {
      // If there are any licensed books by this person then we use 'Also appears in...'
      let heading = 'Appears in';
      if (licensed_book_count) {
        heading = 'Also Appears In';
      }
      return <AuthorBooks authorId={id} as="contributor" heading={heading} />;
    }
    return null;
  }

  render() {
    const { shouldShowSideAdvert } = this.state;
    const { displayType, forceSsr } = this.props;
    const {
      id,
      first_name,
      last_name,
      title,
      avatar_name,
      website,
      is_author,
      facebook,
      twitter,
      instagram,
      spotify,
    } = this.props.person;

    const { debug, baseUrl } = this.props;
    const personName = `${first_name} ${last_name}`;
    const avatarImage = assets.avatarUrl(avatar_name, {
      resize: 'fit-in',
      width: 200,
      height: 200,
    });
    const currentPath = route.person(id, is_author);

    const COVER_IMAGE_SIZE = 200;

    return (
      <div className="person">
        <HeadContent
          contents={{
            content: avatarImage,
            width: COVER_IMAGE_SIZE,
            height: COVER_IMAGE_SIZE,
            url: currentPath,
          }}
        />
        <div className="person__main">
          <header className="person__header">
            <div className="person__header__avatar">
              <Image image={avatarImage} shape="circle" height={200} width={200} alt={personName} />
            </div>
            <div className="person__header__image-buttons">
              <FavouriteButton contentId={id} contentType={CONTENT_TYPES.PERSON} hash={id} />
              <AddToCollectionButton itemId={id} itemType={CONTENT_TYPES.PERSON} />
              <ContentOptions
                heading={personName}
                textToShare={`${personName} on ckbk`}
                urlToShare={baseUrl + currentPath}
                personId={id}
                itemType={CONTENT_TYPES.BOOK}
                subMenu="share"
              >
                <Icon
                  fill={COLOURS.ENERGIZED}
                  name="native-share"
                  width="20"
                  style={{ marginTop: -3 }}
                />
              </ContentOptions>
            </div>
            <Heading level={1} className="heading--size-32">
              {personName}
            </Heading>

            <p className="person__header__title">{title}</p>
            <a href={website} className="link link--energized" target="_blank">
              {website}
            </a>
          </header>
          {this.renderBiography()}
          <TopRecipes
            personId={id}
            viewMoreHref={this.getAllRecipesLink()}
            onViewMore={this.bound.searchPersonRecipes}
            className="margin-bottom-20"
            forceSsr={forceSsr}
          />

          {this.renderSearch()}

          {this.renderAuthorCollections()}
          <BlogPosts
            className="margin-top-40 margin-bottom-20"
            headingLevel={2}
            headingWithDash
            hideIfEmpty
            itemProps={{ allCapsTitle: false, withGutter: true }}
            personId={id}
            limit={10}
            showMoreButton={false}
            skeletonItems={0}
          />
          {displayType === 'mobile' ? (
            <React.Fragment>
              <AuthorBooks authorId={id} heading={`Books by ${personName}`} />
              {this.renderAppearsIn()}
            </React.Fragment>
          ) : null}
          <div>{this.renderAuthorFavourites()}</div>
          <AuthorFollowLinks
            facebook={facebook}
            twitter={twitter}
            instagram={instagram}
            personName={personName}
            personFirstName={first_name}
            spotify={spotify}
          />
          <RelatedPeople authorId={id} />
          {debug ? <RelatedPeople authorId={id} variant="combined" /> : null}
          {debug ? <RelatedPeople authorId={id} variant="favourite" /> : null}
          {debug ? <RelatedPeople authorId={id} variant="recommendation" /> : null}
        </div>
        {displayType !== 'mobile' ? (
          <div className="person__aside">
            <AuthorBooks authorId={id} heading={`Books by ${personName}`} />
            {this.renderAppearsIn()}
            {/* We measure the location of this div to decide whether to render a side advert */}
            <div ref={this.advertRef} />
            {shouldShowSideAdvert ? (
              <Advert
                className="margin-bottom-20 margin-top-40"
                slotName="person-aside"
                format="vertical"
                fullWidthResponsive={false}
                hideOnMobile
                ezoicPlaceholderId={104}
                lazy
              />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  baseUrl: state.config.baseUrl,
  displayType: state.dimensions.displayType,
});

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(withStyles(CssPerson)(Person)));
